import React, { useEffect, useState } from "react";
import { Button, TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DatePickerWithTimeComponent from "../DatePickerWithTimeComponent";
import { addDoc, collection, doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../contexts/AuthContext";
import { showCustomToast } from "../Misc/CustomToast";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import Chip from "@mui/material/Chip";
import UploaderDocForNote from "../Updloader/UploaderDocForNote";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import ImageIcon from "@mui/icons-material/Image";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import MovieCreationIcon from "@mui/icons-material/MovieCreation";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

function AddCBTNote({ closemodal, clientId, firstName, lastName, profilePicture }) {
  const { currentUser } = useAuth();

  const [sessionDate, setSessionDate] = useState(new Date());
  const [sessionNumber, setSessionNumber] = useState("");
  const [presentation, setPresentation] = useState("");
  const [treatmentGoal, setTreatmentGoal] = useState("");
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const draftKey = `draft:cbtNote:${currentUser?.uid || "anon"}`;
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const draftDelay = 800;

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    const filtered = selectedFiles.filter((f) => f.size <= 50 * 1024 * 1024);
    const rejected = selectedFiles.filter((f) => f.size > 50 * 1024 * 1024);
    if (rejected.length) {
      showCustomToast("Some files exceed 50MB and were skipped.", "error");
    }
    setFiles((prev) => [...prev, ...filtered]);
  };

  const uploadFilesAndGetUrls = async (docId) => {
    const storage = getStorage();
    const fileUrls = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const prefix = clientId ? `clients/${clientId}` : "cbtNotes";
      const filePath = `Documents/${prefix}/${docId}/${file.name}`;
      const storageRef = ref(storage, filePath);
      await uploadBytes(storageRef, file);
      const fileUrl = await getDownloadURL(storageRef);
      fileUrls.push({ url: fileUrl, name: file.name });
    }
    return fileUrls;
  };

  const handleAddNote = async () => {
    if (!sessionDate || !sessionNumber || !presentation || !treatmentGoal) {
      showCustomToast("Please fill all mandatory fields.", "error");
      return;
    }
    setIsLoading(true);
    try {
      const baseData = {
        noteType: "cbtNote",
        sessionDateTime: sessionDate,
        sessionNumber,
        presentation,
        treatmentGoal,
        createdAt: serverTimestamp(),
        doneBy: currentUser?.displayName || currentUser?.email || "Unknown",
        clientId: clientId || null,
        clientFirstName: firstName || null,
        clientLastName: lastName || null,
        clientProfilePicture: profilePicture || "",
      };
      const docRef = await addDoc(collection(db, "notes"), baseData);
      const fileUrls = await uploadFilesAndGetUrls(docRef.id);
      if (fileUrls.length) {
        await updateDoc(doc(db, "notes", docRef.id), { fileUrls });
      }
      try {
        localStorage.removeItem(draftKey);
      } catch {}
      showCustomToast("CBT note added successfully.", "success");
      closemodal();
    } catch (e) {
      console.error(e);
      showCustomToast("Error adding CBT note.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Restore draft
  useEffect(() => {
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.sessionDate) {
          const d = new Date(parsed.sessionDate);
          if (!isNaN(d)) setSessionDate(d);
        }
        if (parsed?.sessionNumber) setSessionNumber(parsed.sessionNumber);
        if (parsed?.presentation) setPresentation(parsed.presentation);
        if (parsed?.treatmentGoal) setTreatmentGoal(parsed.treatmentGoal);
      }
    } catch (e) {
      console.warn("CBT draft load error:", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Autosave
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        localStorage.setItem(
          draftKey,
          JSON.stringify({
            sessionDate: sessionDate?.toISOString?.() || null,
            sessionNumber,
            presentation,
            treatmentGoal,
          })
        );
        setIsSavingDraft(false);
      } catch (e) {
        console.warn("CBT draft save error:", e);
      }
    }, draftDelay);
    setIsSavingDraft(true);
    return () => clearTimeout(t);
  }, [sessionDate, sessionNumber, presentation, treatmentGoal, draftKey]);

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <div className="line1-modal-percent">
        <div className="modal-title">Add CBT Note</div>
        <CloseIcon
          style={{
            marginRight: "16px",
            color: "rgb(214, 213, 213)",
            cursor: "pointer",
          }}
          onClick={closemodal}
        />
      </div>

      <div
        style={{
          width: "100%",
          marginTop: "10px",
          height: "75%",
          overflowY: "scroll",
          overflowX: "hidden",
          padding: "12px 16px 0",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            background: "#f8f9fb",
            borderRadius: 12,
            padding: "16px 18px",
            border: "1px solid #e5e7eb",
          }}
        >
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              alignItems: "center",
            }}
          >
            <div style={{ minWidth: 180, maxWidth: 220 }}>
              <div className="label-textfield" style={{ marginBottom: 6 }}>
                Session number
              </div>
              <TextField
                value={sessionNumber}
                onChange={(e) => setSessionNumber(e.target.value)}
                size="small"
                fullWidth
                style={{
                  borderRadius: "5px",
                  backgroundColor: "#f2eeef",
                }}
                placeholder="e.g. 3"
              />
            </div>
            <div style={{ minWidth: 240 }}>
              <DatePickerWithTimeComponent
                title="Session date & time"
                value={sessionDate}
                onChange={setSessionDate}
              />
            </div>
          </div>

          <div>
            <div className="label-textfield" style={{ marginBottom: 6 }}>
              Presentation
            </div>
            <TextField
              value={presentation}
              onChange={(e) => setPresentation(e.target.value)}
              style={{
                width: "100%",
                borderRadius: "5px",
                backgroundColor: "#f2eeef",
              }}
              placeholder="Describe presentation"
              multiline
              rows={4}
            />
          </div>
          <div>
            <div className="label-textfield" style={{ marginBottom: 6 }}>
              Treatment goal worked on
            </div>
            <TextField
              value={treatmentGoal}
              onChange={(e) => setTreatmentGoal(e.target.value)}
              style={{
                width: "100%",
                borderRadius: "5px",
                backgroundColor: "#f2eeef",
              }}
              placeholder="Describe treatment goal"
              multiline
              rows={4}
            />
          </div>
          <div
            style={{
              background: "#fff",
              border: "1px dashed #cbd5e1",
              borderRadius: 10,
              padding: 12,
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Attachments</div>
            {files?.length ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: 8,
                  marginBottom: 8,
                }}
              >
                {files.map((f, idx) => {
                  const ext = f.name.split(".").pop()?.toLowerCase();
                  const getIcon = () => {
                    if (["pdf"].includes(ext)) return <PictureAsPdfIcon sx={{ color: "#d32f2f" }} />;
                    if (["png", "jpg", "jpeg", "gif", "bmp", "webp"].includes(ext))
                      return <ImageIcon sx={{ color: "#1976d2" }} />;
                    if (["mp3", "wav", "aac", "ogg", "m4a"].includes(ext))
                      return <AudiotrackIcon sx={{ color: "#8e24aa" }} />;
                    if (["mp4", "mov", "avi", "mkv", "webm"].includes(ext))
                      return <MovieCreationIcon sx={{ color: "#6d4c41" }} />;
                    if (["zip", "rar", "7z"].includes(ext)) return <FolderZipIcon sx={{ color: "#f57c00" }} />;
                    if (["doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt", "csv"].includes(ext))
                      return <DescriptionIcon sx={{ color: "#2e7d32" }} />;
                    return <InsertDriveFileIcon sx={{ color: "#607d8b" }} />;
                  };
                  return (
                    <div
                      key={idx}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "8px 10px",
                        borderRadius: 8,
                        border: "1px solid #e5e7eb",
                        background: "#f8fafc",
                      }}
                    >
                      {getIcon()}
                      <div style={{ fontSize: 13, color: "#111" }}>{f.name}</div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
                No new files selected
              </div>
            )}
            <UploaderDocForNote handleFileChange={handleFileChange} files={files} />
            <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
              Max file size: 50 MB each.
            </div>
          </div>
        </div>
      </div>
      <div
        className="line4-modal-percent"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 10,
        }}
      >
        <Chip
          label={
            isSavingDraft
              ? "Saving draft..."
              : sessionNumber || presentation || treatmentGoal
              ? "Draft saved"
              : "No draft"
          }
          size="small"
          sx={{
            backgroundColor: isSavingDraft
              ? "#f9a825"
              : sessionNumber || presentation || treatmentGoal
              ? "#2e7d32"
              : "#607d8b",
            color: "#fff",
            fontWeight: 700,
          }}
        />
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            try {
              localStorage.removeItem(draftKey);
            } catch {}
            setSessionNumber("");
            setSessionDate(new Date());
            setPresentation("");
            setTreatmentGoal("");
            setFiles([]);
          }}
        >
          Clear draft
        </Button>
        <Button
          variant="contained"
          style={{ backgroundColor: "white", color: "black" }}
          onClick={closemodal}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAddNote}
          disabled={isLoading}
          style={
            isLoading
              ? { backgroundColor: "red", color: "white" }
              : { backgroundColor: "#1565C0", color: "white" }
          }
        >
          {isLoading ? "Saving..." : "Add CBT Note"}
        </Button>
      </div>
    </div>
  );
}

export default AddCBTNote;
